import apiClient from "./apiClient";
import {
  AcquisitionCost,
  AcquisitionCostInput,
  CohortData,
  UnitEconomicsData,
} from "../types/unit-economics.types";

/**
 * Get unit economics metrics for the selected period.
 * Returns current and previous (equal-length, directly preceding) period values,
 * plus a 12-month trend series per metric.
 *
 * Granularity is no longer sent — the backend cache key is (brandId, from, to)
 * and the previous period is always derived from `to − from`.
 *
 * @param from - ISO date string (inclusive start)
 * @param to - ISO date string (exclusive end)
 */
export const getUnitEconomics = async (params: {
  from: string;
  to: string;
}): Promise<UnitEconomicsData> => {
  const { data } = await apiClient.get<UnitEconomicsData>(
    "/kiedynabory/settlements/unit-economics",
    { params }
  );
  return data;
};

/**
 * Get cohort-LTV data (cumulative revenue per customer by cohort age).
 * Independent endpoint from the metrics grid — own loading/error/empty states.
 *
 * @param from - ISO date string (inclusive start)
 * @param to - ISO date string (exclusive end)
 */
export const getUnitEconomicsCohorts = async (params: {
  from: string;
  to: string;
}): Promise<CohortData[]> => {
  const { data } = await apiClient.get<CohortData[]>(
    "/kiedynabory/settlements/unit-economics/cohorts",
    { params }
  );
  return data;
};

/**
 * List acquisition cost entries.
 * Available to admin and admin:kiedynabory (read-only).
 */
export const getAcquisitionCosts = async (): Promise<AcquisitionCost[]> => {
  const { data } = await apiClient.get<AcquisitionCost[]>(
    "/kiedynabory/settlements/acquisition-costs"
  );
  return data;
};

/**
 * Create an acquisition cost entry. Admin-only endpoint.
 */
export const createAcquisitionCost = async (
  input: AcquisitionCostInput
): Promise<AcquisitionCost> => {
  const { data } = await apiClient.post<AcquisitionCost>(
    "/kiedynabory/settlements/acquisition-costs",
    input
  );
  return data;
};

/**
 * Update an acquisition cost entry. Admin-only endpoint.
 */
export const updateAcquisitionCost = async (
  id: string,
  input: AcquisitionCostInput
): Promise<AcquisitionCost> => {
  const { data } = await apiClient.patch<AcquisitionCost>(
    `/kiedynabory/settlements/acquisition-costs/${id}`,
    input
  );
  return data;
};

/**
 * Delete an acquisition cost entry. Admin-only endpoint.
 */
export const deleteAcquisitionCost = async (id: string): Promise<void> => {
  await apiClient.delete(`/kiedynabory/settlements/acquisition-costs/${id}`);
};
